import { camera } from '../utils/camera_manager';

// The camera stream itself is owned by camera_manager.js (one stream for the whole session, moved
// between screens so the permission prompt appears once). This file is the glue: it tells the manager
// when a scan screen appears/disappears and what to do with a decoded code.

// Where a decoded code goes. Reads #scan-wrapper live at scan time, so it always uses the current
// screen's configuration regardless of which screen the persistent camera is mounted in.
function onScanSuccess(decodedText) {
  const wrapper = document.getElementById('scan-wrapper');
  if (!wrapper) { return; }

  // Put-away step 1: reject a Platz-QR from a different Lager than the one chosen for this put-away.
  // The current Lager's storage_identifier is on the wrapper; the scanned code's first segment is the
  // Lager it belongs to. On a mismatch, open the "falsches Lager" modal (Lager wechseln / Erneut
  // scannen) instead of navigating, so a foreign Platz can never be silently selected.
  const depotGuard = wrapper.getAttribute('data-depot-storage-identifier');
  const mismatchUrl = wrapper.getAttribute('data-depot-mismatch-url');
  if (depotGuard && mismatchUrl) {
    const scannedDepot = getStorageArray(decodedText)[0];
    if (scannedDepot && String(scannedDepot) !== String(depotGuard)) {
      up.layer.open({ url: addPutAwayScanParams(mismatchUrl, decodedText), mode: 'modal', target: '.scan-mismatch' });
      return;
    }
  }

  const action = wrapper.getAttribute('scanaction');
  const target = wrapper.getAttribute('scantarget');
  const preprocessor = wrapper.getAttribute('scanpreprocessor');
  const upTarget = wrapper.getAttribute('up-target');

  if (action === 'url') {
    var relativeUrl = target;
    if (preprocessor) {
      relativeUrl = getPreprocessor(preprocessor)(relativeUrl, decodedText);
    } else {
      const params = new URLSearchParams({ 'qr': decodedText });
      relativeUrl = `${target}?${params.toString()}`;
    }

    const layerMode = wrapper.getAttribute('up-mode');
    if (layerMode) {
      // Open the scan result (e.g. the quantity dialog) as a full-screen overlay so it covers the
      // whole picking screen. The overlay auto-closes when it lands on `up-accept-location` (the
      // transfer edit URL the controller redirects to on save), then reloads the named fragments.
      const openOptions = { url: relativeUrl, mode: layerMode };
      const size = wrapper.getAttribute('up-size');
      if (size) { openOptions.size = size; }
      const acceptLocation = wrapper.getAttribute('up-accept-location');
      if (acceptLocation) { openOptions.acceptLocation = acceptLocation; }
      const reloadTargets = wrapper.getAttribute('data-reload-on-accepted');
      if (reloadTargets) {
        openOptions.onAccepted = () => up.reload(reloadTargets, { layer: 'root' });
      }
      up.layer.open(openOptions);
    } else {
      const options = { 'layer': 'closest' };
      if (upTarget) {
        options['target'] = upTarget;
      }
      up.visit(relativeUrl, options);
    }
  } else if (action === 'accept') {
    up.layer.accept(decodedText);
  }
}

camera.configure({ onScan: onScanSuccess });

// A scan screen appeared: claim the camera for the front layer's reader (no-op if already mounted).
up.compiler('#barcode-reader', (element) => {
  camera.syncToFrontLayer();
  return () => camera.handleReaderRemoved(element);
});

// The reload button shown while the camera is loading / after an error: force a fresh stream.
up.compiler('.reload-scanner', (element) => {
  element.addEventListener('click', () => camera.restart());
});

// Picking screen: the search field hides the camera (transfer-picking_compiler.js fires these).
document.addEventListener('scanner:stop', () => camera.detach());
document.addEventListener('scanner:start', () => camera.syncToFrontLayer());

// Re-home the camera as overlays open and close. Opening: the new front layer either has its own
// reader (claimed) or none (camera parked). Closing: re-attach to the revealed screen's reader.
up.on('up:layer:opened', () => camera.syncToFrontLayer());
up.on('up:layer:accepted', () => setTimeout(() => camera.syncToFrontLayer(), 300));
up.on('up:layer:dismissed', () => setTimeout(() => camera.syncToFrontLayer(), 300));
up.on('up:layer:location:changed', () => camera.syncToFrontLayer());

function getPreprocessor(proc) {
  if (proc === 'getDepot') { return getDepot; }
  if (proc === 'getLocation') { return getLocation; }
  if (proc === 'getSpot') { return getSpot; }
  if (proc === 'getStorageArray') { return getStorageArray; }
  if (proc === 'addTransferItemParams') { return addTransferItemParams; }
  if (proc === 'addStorageSpotLookupParams') { return addStorageSpotLookupParams; }
  if (proc === 'addPutAwayScanParams') { return addPutAwayScanParams; }
}

function getDepot(code) {
  return getStorageArray(code)[0];
}

function getLocation(code) {
  return getStorageArray(code)[1];
}

function getSpot(code) {
  return getStorageArray(code)[2];
}

window.getStorageArray = function(code) {
  const [, depot, location, spot] = code.match(/stadler-app--stloc-(\d+)-(\d+)-(\d+)/) || [];
  return [depot, location, spot].map(Number);
}

function addTransferItemParams(url, code) {
  const transfer_id = document.querySelector('.add-by-scan').getAttribute('transfer_id');
  const params = `warehousemanagement_transfer_item[transfer_id]=${transfer_id}&warehousemanagement_transfer_item[qr_code]=${code}`;
  return `${url}?${params}`;
}

function addStorageSpotLookupParams(url, code) {
  const [depot, location, spot] = getStorageArray(code);
  const params = `warehousemanagement_storage_spot[lookup_depot_storage_identifier]=${depot}&warehousemanagement_storage_spot[lookup_storage_location_index]=${location}&warehousemanagement_storage_spot[lookup_storage_spot_index]=${spot}`;
  return `${url}?${params}`;
}

// Put-away step 1: turn a scanned Platz-QR into the flat lookup_* params the spot action resolves.
// Called by onScanSuccess as (target, decodedText). The scantarget already carries ?depot_id=, so
// the params are appended with '&'; a full-page up.visit then re-renders step 1 with the spot picked.
function addPutAwayScanParams(url, code) {
  const [depot, location, spot] = getStorageArray(code);
  if (!location || !spot) { return url; }
  const params = `lookup_depot_storage_identifier=${depot}&lookup_storage_location_index=${location}&lookup_storage_spot_index=${spot}`;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params}`;
}
